import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { API } from "../../constants/API";

export interface BoUnitRecord {
  ISBN: string;
  Total_BO_Units: number;
  DISTRIBUTION_CHANNEL: string | null;
  DISTRIBUTION_CHANNEL_NAME: string | null;
  ORGANIZATION_ID: string | null;
  ORG_NAME: string | null;
  ACCOUNT_NBR: string | null;
  ACCOUNT_NAME: string | null;
  Organization_Label?: string | null;
  Account_Label?: string | null;
  REASON?: string | null;
  // Optional flag to identify summary/total rows added on the client side
  isTotalRow?: boolean;
}

interface BoUnitsApiResponse {
  isbn: string;
  totalRows: number;
  data: BoUnitRecord[];
}

interface BoUnitsState {
  isbn: string;
  totalRows: number;
  data: BoUnitRecord[];
  status: string;
  statusCode: number;
  error: string | null;
  isLoading: boolean;
}

const initialState: BoUnitsState = {
  isbn: "",
  totalRows: 0,
  data: [],
  status: "",
  statusCode: 0,
  error: null,
  isLoading: false,
};

export const fetchBoUnitsDetails = createAsyncThunk(
  "boUnits/fetchDetails",
  async (
    payload: { 
      SelectedEbookIsbn: string;
      sortBy?: { heading: string; sortType: 'asc' | 'desc' };
    },
    { rejectWithValue }
  ) => {
    try {
      console.log('fetchBoUnitsDetails - Sending payload:', JSON.stringify(payload));
      console.log('fetchBoUnitsDetails - sortBy object:', payload.sortBy);
      const response = await axios.post(API.fetchBoUnitsDetails, payload);
      console.log('fetchBoUnitsDetails - Response received, data length:', response.data?.data?.length);
      return response.data as BoUnitsApiResponse;
    } catch (err: any) {
      const errorPayload = err?.response?.data ?? err?.message ?? "Unknown error";
      return rejectWithValue(errorPayload);
    }
  }
);

const boUnitsDetailsSlice = createSlice({
  name: "boUnitsDetails",
  initialState,
  reducers: {
    clearBoUnitsDetails: (state) => {
      state.data = [];
      state.totalRows = 0;
      state.isbn = "";
      state.status = "";
      state.statusCode = 0;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBoUnitsDetails.pending, (state) => {
        state.isLoading = true;
        state.status = "Loading";
        state.error = null;
      })
      .addCase(
        fetchBoUnitsDetails.fulfilled,
        (state, action: PayloadAction<BoUnitsApiResponse>) => {
          state.isLoading = false;
          state.status = "Success";
          state.statusCode = 200;
          state.data = action.payload?.data ?? [];
          state.totalRows = action.payload?.totalRows ?? 0;
          state.isbn = action.payload?.isbn ?? "";
        }
      )
      .addCase(fetchBoUnitsDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.status = "Failed";
        state.statusCode = 500;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch BO units details";
      });
  },
});

export const { clearBoUnitsDetails } = boUnitsDetailsSlice.actions;
export default boUnitsDetailsSlice.reducer;

